import React from 'react';
import { theme } from '../../theme';
import { useLanguage } from '../../i18n/useLanguage';
import type { InfrastructureMode } from '../../types/workplace';

interface InfrastructureStepProps {
  selectedMode: InfrastructureMode | null;
  cloudProvider: string | null;
  onSelectMode: (mode: InfrastructureMode) => void;
  onSelectCloudProvider: (provider: string) => void;
  onContinue: () => void;
}

const MODES: { key: InfrastructureMode; labelKey: string }[] = [
  { key: 'on-premise', labelKey: 'workplace.infrastructure.onPremise' },
  { key: 'cloud', labelKey: 'workplace.infrastructure.cloud' },
  { key: 'saas', labelKey: 'workplace.infrastructure.saas' },
  { key: 'hybrid', labelKey: 'workplace.infrastructure.hybrid' },
];

export const InfrastructureStep: React.FC<InfrastructureStepProps> = ({
  selectedMode,
  cloudProvider,
  onSelectMode,
  onSelectCloudProvider,
  onContinue,
}) => {
  const { t } = useLanguage();

  const canContinue =
    selectedMode !== null &&
    (selectedMode !== 'cloud' || (cloudProvider ?? '').trim().length > 0);

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: 16 }}>
      <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: theme.text }}>
        {t('workplace.infrastructure.title')}
      </h2>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 20 }}>
        {MODES.map(({ key, labelKey }) => {
          const selected = selectedMode === key;
          return (
            <button
              key={key}
              onClick={() => onSelectMode(key)}
              style={{
                padding: '16px 14px',
                borderRadius: 6,
                border: `2px solid ${selected ? theme.primary : theme.border}`,
                background: selected ? theme.primarySoft : '#fff',
                cursor: 'pointer',
                textAlign: 'left',
                fontSize: 14,
                fontWeight: 600,
                color: theme.text,
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              {t(labelKey as any)}
            </button>
          );
        })}
      </div>

      {selectedMode === 'cloud' && (
        <div style={{ marginTop: 16 }}>
          <label
            style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 6, color: theme.text }}
          >
            {t('workplace.infrastructure.cloudFollowUp')}
          </label>
          <input
            type="text"
            value={cloudProvider ?? ''}
            onChange={(e) => onSelectCloudProvider(e.target.value)}
            placeholder="z.B. AWS, Azure, IONOS..."
            style={{
              width: '100%',
              padding: '10px 12px',
              border: `1px solid ${theme.border}`,
              borderRadius: 4,
              fontSize: 14,
              boxSizing: 'border-box',
            }}
          />
        </div>
      )}

      <button
        onClick={onContinue}
        disabled={!canContinue}
        style={{
          marginTop: 20,
          padding: '10px 24px',
          border: `1px solid ${canContinue ? theme.primaryBorder : theme.disabled}`,
          background: canContinue ? theme.primarySoft : '#eee',
          color: canContinue ? theme.text : theme.disabled,
          fontWeight: 700,
          fontSize: 15,
          borderRadius: 4,
          cursor: canContinue ? 'pointer' : 'not-allowed',
        }}
      >
        {t('workplace.infrastructure.continue')}
      </button>
    </div>
  );
};
